import { Flex, Grid, Text, Textarea } from '@chakra-ui/react';
import {
  Button,
  FormFeedback,
  Input,
  Modal,
  TagInput
} from '@morressier/alembic';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { IReviewerSearchProperties, ISearchQueryItems } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import {
  buildEmptyFormValues,
  buildFormValuesFromItem,
  getFieldLabel,
  isTagField,
  LexiconData,
  LexiconFormValues,
  toLexiconData
} from './types';

const NAME_FIELD_PREDICATES = [
  'ReviewerSalutation',
  'ReviewerFirstName',
  'ReviewerMiddleName',
  'ReviewerLastName'
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  item?: ISearchQueryItems | null;
  formFields: IReviewerSearchProperties[];
  onSave: (data: LexiconData) => Promise<boolean>;
}

const ReviewerSearchUserFormModal = ({
  isOpen,
  onClose,
  mode,
  item,
  formFields,
  onSave
}: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<LexiconFormValues>({
    defaultValues: buildEmptyFormValues(formFields)
  });

  useEffect(() => {
    reset(
      mode === 'edit' && item
        ? buildFormValuesFromItem(item, formFields)
        : buildEmptyFormValues(formFields)
    );
  }, [mode, item, formFields, reset]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (values: LexiconFormValues) => {
    setIsSubmitting(true);
    try {
      const ok = await onSave(
        toLexiconData(values, formFields, mode === 'edit')
      );
      if (ok) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const nameFields = formFields.filter(f =>
    NAME_FIELD_PREDICATES.includes(f.predicateLabel)
  );
  const otherFields = formFields.filter(
    f => !NAME_FIELD_PREDICATES.includes(f.predicateLabel)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
      closeButtonProps={{ hidden: false }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          direction="column"
          p={4}
          borderBottom="1px solid"
          borderColor="neutralPrimaryBorder"
        >
          <Text textStyle="headingSection">
            {mode === 'edit'
              ? 'Edit Reviewer Search User'
              : 'Add Reviewer Search User'}
          </Text>
        </Flex>

        <Flex direction="column" gap={4} p={4} maxH="65vh" overflowY="auto">
          <Grid templateColumns="repeat(4, 1fr)" gap={3}>
            {nameFields.map(field => {
              const label = getFieldLabel(field);
              const required = field.config?.required === true;
              const error = errors[field.predicateLabel];

              return (
                <Flex direction="column" gap={2} key={field.predicateLabel}>
                  <FieldLabel label={label} subText="" isRequired={required} />
                  <Input
                    {...register(
                      field.predicateLabel,
                      required ? { required: `${label} is required` } : {}
                    )}
                    isInvalid={!!error}
                  />
                  {error && (
                    <FormFeedback variant="error">
                      {String(error.message)}
                    </FormFeedback>
                  )}
                </Flex>
              );
            })}
          </Grid>

          {otherFields.map(field => {
            const label = getFieldLabel(field);
            const required = field.config?.required === true;
            const error = errors[field.predicateLabel];

            if (isTagField(field)) {
              return (
                <Flex direction="column" gap={2} key={field.predicateLabel}>
                  <FieldLabel
                    label={label}
                    subText=""
                    isRequired={required}
                    helpText={field.config?.tooltip}
                  />
                  <Controller
                    control={control}
                    name={field.predicateLabel}
                    rules={
                      required
                        ? {
                            validate: (value: unknown) =>
                              (value as { text: string }[] | undefined)?.length
                                ? true
                                : `${label} is required`
                          }
                        : undefined
                    }
                    render={({ field: rhfField, fieldState }) => (
                      <TagInput
                        {...(rhfField as any)}
                        control={control as any}
                        isInvalid={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                        placeholder="Type and press Enter to add"
                        helperText="Press Enter to add"
                      />
                    )}
                  />
                </Flex>
              );
            }

            return (
              <Flex direction="column" gap={2} key={field.predicateLabel}>
                <FieldLabel
                  label={label}
                  subText=""
                  isRequired={required}
                  helpText={field.config?.tooltip}
                />
                {field.config?.controlType === 'textarea' ? (
                  <Textarea
                    {...register(
                      field.predicateLabel,
                      required ? { required: `${label} is required` } : {}
                    )}
                    isInvalid={!!error}
                  />
                ) : (
                  <Input
                    {...register(
                      field.predicateLabel,
                      required ? { required: `${label} is required` } : {}
                    )}
                    isInvalid={!!error}
                  />
                )}
                {error && (
                  <FormFeedback variant="error">
                    {String(error.message)}
                  </FormFeedback>
                )}
              </Flex>
            );
          })}
        </Flex>

        <Flex
          justifyContent="flex-end"
          gap={2}
          p={4}
          borderTop="1px solid"
          borderColor="neutralPrimaryBorder"
        >
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            {mode === 'edit' ? 'Save Changes' : 'Add User'}
          </Button>
        </Flex>
      </form>
    </Modal>
  );
};

export default ReviewerSearchUserFormModal;
